import { DataSelector } from '@atoms/dataSelectors/dataSelector/dataSelector';
import { InputField } from '@atoms/input';
import BlockBase from '@components/dnd/base/blockBase';
import { Column } from '@components/dnd/base/blockBase/body.block.style';
import { useAtom } from '@hooks/useAtom';
import EndControlsFB, {
  type SettingsConfig
} from '@library/formBuilder/endControls';
import { FormControl, FormLabel, Radio, RadioGroup } from '@mui/joy';
import { type DNDBlock, ResolvedVariableType } from '@stratumn/studio-shared';
import { observer } from 'mobx-react';
import type { ChangeEvent } from 'react';
import { useParams } from 'react-router-dom';
import { ParamsList } from '@/routes/routes.types';

import { initialCheckBoxData } from './checkbox.data';
import { CheckBoxModal } from './checkbox.modal';
import {
  CheckBoxIcon,
  type CheckBoxTypeOption,
  checkBoxTypeOptions,
  isCorrectCheckBoxType
} from './checkbox.utils';

const CheckBoxField = (block: DNDBlock) => {
  const actionId = useParams()[ParamsList.ActionId] as string;

  const atom = useAtom({
    parentId: actionId,
    initialData: initialCheckBoxData,
    type: 'Check Box',
    sourceId: block.id,
    parentKind: 'form_action',
    variableInfo: {
      //? will change depending on checkbox type
      resolvedType: ResolvedVariableType.List
    }
  });

  if (!atom) {
    return null;
  }

  const getSettingsModal = (): SettingsConfig => ({
    title: atom.data.title || 'Check Box title',
    content: <CheckBoxModal atom={atom} />
  });

  const getEndControls = () => (
    <EndControlsFB
      dataItem={atom}
      dndBlock={block}
      hasRequired
      settings={getSettingsModal()}
    />
  );

  const onCheckBoxTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!isCorrectCheckBoxType(e.target.value)) {
      return;
    }
    const newCheckBoxType = e.target.value;
    atom.data.checkboxType = newCheckBoxType;
    if (newCheckBoxType === 'radio') {
      // @ts-expect-error at this point, we know that variableInfo is defined
      atom.variableInfo.resolvedType = ResolvedVariableType.Text;
    } else {
      // @ts-expect-error at this point, we know that variableInfo is defined
      atom.variableInfo.resolvedType = ResolvedVariableType.List;
    }
  };

  return (
    <BlockBase
      dataItem={atom}
      dndBlock={block}
      endControls={getEndControls()}
      icon={CheckBoxIcon}
      title={atom.data.title}
    >
      <Column>
        <InputField
          label="Field title"
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            atom.data.title = e.target.value;
          }}
          placeholder="Aa"
          required
          value={atom.data.title}
        />
      </Column>

      <Column $width="100px">
        <FormControl size="sm">
          <FormLabel>Checkbox type</FormLabel>
          <RadioGroup name="radio-buttons-group" value={atom.data.checkboxType}>
            {checkBoxTypeOptions.map((option: CheckBoxTypeOption) => (
              <Radio
                color="neutral"
                disabled={option.isDisabled ?? false}
                key={option.value}
                label={option.name}
                onChange={onCheckBoxTypeChange}
                size="sm"
                value={option.value}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Column>
      <DataSelector
        dataSource={atom.data.dataSource}
        id={atom.id}
        onDataSourceChange={v => {
          atom.data.dataSource = v;
        }}
        onSourceDataChange={v => {
          atom.data.dataSourceData = v;
        }}
        sourcesData={atom.data.dataSourceData}
      />
    </BlockBase>
  );
};

export default observer(CheckBoxField);
